import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Plus, Edit, Trash2, Shield, User, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_INFO = {
  super_admin: {
    label: 'Super Admin',
    description: 'Acceso total a todas las instituciones',
    icon: Shield,
    color: 'text-red-600'
  },
  admin: {
    label: 'Administrador',
    description: 'Gestión completa de la institución',
    icon: Shield,
    color: 'text-blue-600'
  },
  therapist: {
    label: 'Terapeuta',
    description: 'Acceso completo a pacientes y reportes',
    icon: User,
    color: 'text-green-600'
  },
  read_only: {
    label: 'Solo Lectura',
    description: 'Ver datos sin editar',
    icon: Eye,
    color: 'text-gray-600'
  }
};

const UsersManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'therapist',
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Memoize filtered roles to avoid recomputation
  const assignableRoles = useMemo(() => 
    roles.filter(r => r.can_assign),
    [roles]
  );

  // Memoize active users list
  const activeUsers = useMemo(() => 
    users.filter(u => u.is_active !== false),
    [users]
  );

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/roles`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data.roles);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, formData, { withCredentials: true });
      toast.success('Usuario creado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al crear usuario');
      }
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de desactivar este usuario?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('Usuario desactivado');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al desactivar usuario');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: 'therapist' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Gestión de Usuarios</h1>
          <p className="text-gray-600">
            Administra usuarios y roles de tu institución
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre Completo</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="role">Rol</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map((role) => (
                      <SelectItem key={role.role} value={role.role}>
                        {ROLE_INFO[role.role]?.label || role.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full">
                Crear Usuario
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Roles Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {Object.entries(ROLE_INFO).map(([key, info]) => {
          const Icon = info.icon;
          return (
            <Card key={key} className="p-4">
              <div className="flex items-start space-x-3">
                <Icon className={`h-6 w-6 ${info.color}`} />
                <div>
                  <h3 className="font-semibold">{info.label}</h3>
                  <p className="text-sm text-gray-600">{info.description}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Users Table */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Usuarios Activos</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">Nombre</th>
                <th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">Rol</th>
                <th className="text-left py-3 px-4">Fecha Creación</th>
                <th className="text-left py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.map((user) => {
                const roleInfo = ROLE_INFO[user.role] || {};
                const Icon = roleInfo.icon || User;
                
                return (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{user.name}</td>
                    <td className="py-3 px-4">{user.email}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Icon className={`h-4 w-4 ${roleInfo.color}`} />
                        <span>{roleInfo.label || user.role}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(user.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {users.filter(u => u.is_active !== false).length === 0 && (
            <p className="text-center py-8 text-gray-500">No hay usuarios</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default UsersManagementPage;
